/**
 * YUMIX — Premium Injury Adaptation Card.
 *
 * Hero-style dashboard card that surfaces the Injury-to-Workout pipeline
 * front-and-center (no longer hidden in Settings). Tier-aware behaviour:
 *
 *   • PREMIUM / ELITE without active injury plan
 *       → "🩹 Activer le Mode Blessure" CTA, opens InjuryCaptureDialog.
 *   • PREMIUM / ELITE with active injury plan
 *       → "🎉 Mode Blessure actif — Désactiver" CTA, calls restore-previous.
 *   • FREE
 *       → "🔓 Débloquer le Mode Blessure" CTA, opens paywall flow via the
 *         InjuryCaptureDialog (which shows the upgrade message inline).
 *
 * Visual : gradient orange→pink, animated "energy ring", premium feel.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { ShieldPlus, Sparkles, RotateCcw, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage, api } from '../context/AppContext';
import InjuryCaptureDialog from './InjuryCaptureDialog';
import { playCelebrationCue } from '../utils/celebrationSound';

const InjuryAdaptationCard = ({ onPlanChanged }) => {
  const { language } = useLanguage();
  const fr = language.startsWith('fr');
  const [open, setOpen] = useState(false);
  const [activeInjury, setActiveInjury] = useState(null);  // { zone, severity, plan_id, created_at } | null
  const [restoring, setRestoring] = useState(false);
  const [showHealCelebration, setShowHealCelebration] = useState(false);

  // Fetch active injury state. Source of truth = active protocol in
  // `db.training_plans` (post-2026-04-28 fix). `last_injury_event` is kept
  // as a fallback for legacy users whose protocol was never persisted.
  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get('/injury-workout/active');
      const protocol = data?.protocol;
      const legacy = data?.last_injury_event;
      if (protocol && protocol.injury_context?.zone) {
        setActiveInjury({
          zone: protocol.injury_context.zone,
          severity: protocol.injury_context.severity,
          plan_id: protocol.plan_id,
          created_at: protocol.created_at,
        });
      } else if (legacy) {
        setActiveInjury(legacy);
      } else {
        setActiveInjury(null);
      }
    } catch {
      // Silent — non-critical
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Handler : Toggle OFF — restore the regular plan
  const handleHealAndRestore = async () => {
    setRestoring(true);
    try {
      const { data } = await api.post('/injury-workout/restore-previous');
      if (data?.restored) {
        // 🎉 Healing celebration!
        setActiveInjury(null);
        setShowHealCelebration(true);
        playCelebrationCue();
        toast.success(
          fr
            ? '🎉 Félicitations pour ta guérison ! Ton programme régulier est de retour. Let\'s go !'
            : '🎉 Congrats on your recovery! Your regular plan is back. Let\'s go!',
          { duration: 8000 }
        );
        onPlanChanged && onPlanChanged();
        setTimeout(() => setShowHealCelebration(false), 4500);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || (fr ? 'Aucun plan précédent à restaurer' : 'No previous plan to restore'));
    } finally {
      setRestoring(false);
    }
  };

  const isInjuryActive = !!activeInjury;
  const zoneLabel = activeInjury?.zone
    ? activeInjury.zone.replace('_', ' ').replace(/(^|\s)(\w)/g, (m) => m.toUpperCase())
    : '';

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative mb-4"
        data-testid="injury-adaptation-card"
      >
        {/* Pulsing energy ring (premium feel) */}
        <motion.div
          aria-hidden
          className="absolute -inset-px rounded-2xl opacity-50"
          style={{
            background: isInjuryActive
              ? 'linear-gradient(135deg, #fbbf24, #f97316, #ec4899)'
              : 'linear-gradient(135deg, #f97316, #ec4899, #8b5cf6)',
            filter: 'blur(8px)',
          }}
          animate={{ opacity: [0.35, 0.6, 0.35] }}
          transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
        />

        <div className="relative rounded-2xl bg-card border border-orange-500/30 overflow-hidden">
          <div className="p-3">
            <div className="flex items-center gap-2.5">
              {/* Compact icon */}
              <div className="relative flex-shrink-0">
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center shadow-md shadow-orange-500/40">
                  {isInjuryActive
                    ? <ShieldPlus className="w-4 h-4 text-white" />
                    : <Sparkles className="w-4 h-4 text-white" />}
                </div>
              </div>

              {/* Title + sub — short */}
              <div className="flex-1 min-w-0">
                <h3 className="font-heading text-sm uppercase tracking-tight leading-none">
                  {isInjuryActive
                    ? (fr ? 'Mode Blessure' : 'Injury Mode')
                    : (fr ? 'Mode Blessure' : 'Injury Mode')}
                </h3>
                <p className="text-[10px] text-muted-foreground mt-0.5 leading-tight truncate">
                  {isInjuryActive
                    ? (fr ? `Actif · ${zoneLabel}` : `Active · ${zoneLabel}`)
                    : (fr ? "Plan adapté en 15s par l'IA" : 'AI adapts your plan in 15s')}
                </p>
              </div>

              {/* Toggle button */}
              {isInjuryActive ? (
                <Button
                  size="sm"
                  onClick={handleHealAndRestore}
                  disabled={restoring}
                  className="shrink-0 bg-gradient-to-r from-emerald-500 to-teal-500 hover:opacity-90 font-bold uppercase tracking-widest text-white text-[10px] px-3 h-8"
                  data-testid="heal-restore-btn"
                >
                  {restoring ? <Loader2 className="w-3 h-3 animate-spin" /> : (fr ? 'Désactiver' : 'Disable')}
                </Button>
              ) : (
                <Button
                  size="sm"
                  onClick={() => setOpen(true)}
                  className="shrink-0 bg-gradient-to-r from-orange-500 via-pink-500 to-violet-500 hover:opacity-90 font-bold uppercase tracking-widest text-white text-[10px] px-3 h-8"
                  data-testid="open-injury-adaptation"
                >
                  {fr ? 'Activer' : 'Enable'}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* 🎉 Healing celebration overlay */}
        <AnimatePresence>
          {showHealCelebration && (
            <motion.div
              key="heal-celebration"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="absolute inset-0 rounded-2xl flex flex-col items-center justify-center bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500 z-10"
              data-testid="heal-celebration"
            >
              <motion.div
                animate={{ rotate: [0, 360], scale: [1, 1.1, 1] }}
                transition={{ duration: 2.5, repeat: Infinity }}
              >
                <RotateCcw className="w-12 h-12 text-white drop-shadow-lg" />
              </motion.div>
              <p className="text-white font-heading text-lg uppercase tracking-tight mt-2 text-center px-4">
                {fr ? '🎉 Félicitations pour ta guérison !' : '🎉 Congrats on your recovery!'}
              </p>
              <p className="text-white/90 text-xs mt-1 text-center px-4">
                {fr ? 'Ton programme régulier est de retour. Let\'s go !' : 'Your regular plan is back. Let\'s go!'}
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      <InjuryCaptureDialog
        open={open}
        onOpenChange={setOpen}
        onPlanGenerated={() => { refresh(); onPlanChanged && onPlanChanged(); }}
      />
    </>
  );
};

export default InjuryAdaptationCard;
